'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import ToolCatalogCardGrid, { emptyCatalogSummary } from '@/components/tools/ToolCatalogCardGrid';
import { CATALOG_INITIAL_VISIBLE, CATALOG_LOAD_MORE_STEP } from '@/lib/tool-catalog-constants';
import type { ToolCatalogLookup, ToolCatalogSummary } from '@/lib/tool-catalog-types';
import type { PublicToolCatalogItem, PublicToolCategorySlug } from '@/lib/tool-categories';
import { CATEGORY_CHIP_LABELS, CATEGORY_CHIP_ORDER } from '@/lib/tool-category-chips';
import type { ToolCatalogItem } from '@/lib/tools';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  startTransition,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

type ToolCatalogFilter = 'all' | 'favorites' | PublicToolCategorySlug;
type ToolCatalogSort = 'recommended' | 'usage-desc' | 'usage-asc' | 'rating-desc' | 'rating-asc';

type ToolAppCatalogProps = {
  entries: PublicToolCatalogItem[];
  eyebrow?: string;
  title?: string;
  description?: string;
  className?: string;
  showFilters?: boolean;
  initialFilter?: string;
  initialSort?: string;
  initialQuery?: string;
  initialLookup?: ToolCatalogLookup;
};

const filterOrder: ToolCatalogFilter[] = ['all', 'favorites', ...CATEGORY_CHIP_ORDER];

const filterLabels: Record<ToolCatalogFilter, string> = {
  all: 'All',
  favorites: 'Favorites',
  ...CATEGORY_CHIP_LABELS,
};

const sortLabels: Record<ToolCatalogSort, string> = {
  recommended: 'Recommended',
  'usage-desc': 'Most used',
  'usage-asc': 'Least used',
  'rating-desc': 'Highest rated',
  'rating-asc': 'Lowest rated',
};

const TOOL_CATALOG_LOOKUP_BATCH_SIZE = 48;

function parseCatalogFilter(
  value: string | null,
  availableFilters: ToolCatalogFilter[],
  showFilters: boolean,
): ToolCatalogFilter {
  if (!showFilters || !value) {
    return 'all';
  }

  return availableFilters.includes(value as ToolCatalogFilter)
    ? (value as ToolCatalogFilter)
    : 'all';
}

function parseCatalogSort(value: string | null): ToolCatalogSort {
  if (!value) {
    return 'recommended';
  }

  return value in sortLabels ? (value as ToolCatalogSort) : 'recommended';
}

function ToolCatalogSearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <path d="M16 16l5 5" fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function ToolCatalogClearIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function matchesFilter(
  item: PublicToolCatalogItem,
  filter: ToolCatalogFilter,
  favoriteSlugs: Set<ToolCatalogItem['slug']>,
) {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'favorites') {
    return favoriteSlugs.has(item.slug);
  }

  return item.category === filter;
}

function getRatingSortValue(summary: ToolCatalogSummary) {
  if (summary.rating.average === null || summary.rating.count === 0) {
    return null;
  }

  return summary.rating.average;
}

function compareByRating(
  left: ToolCatalogSummary,
  right: ToolCatalogSummary,
  direction: 'asc' | 'desc',
) {
  const leftValue = getRatingSortValue(left);
  const rightValue = getRatingSortValue(right);
  const leftHasRating = leftValue !== null;
  const rightHasRating = rightValue !== null;

  if (leftHasRating !== rightHasRating) {
    return leftHasRating ? -1 : 1;
  }

  if (!leftHasRating || !rightHasRating) {
    return 0;
  }

  if (leftValue !== rightValue) {
    return direction === 'desc' ? rightValue - leftValue : leftValue - rightValue;
  }

  return right.rating.count - left.rating.count;
}

function ToolAppCatalogInner({
  entries,
  eyebrow,
  title,
  description,
  className,
  showFilters = true,
  initialFilter,
  initialSort,
  initialQuery,
  initialLookup,
}: ToolAppCatalogProps) {
  const { user } = useFirebaseAuth();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchId = useId();
  const [activeFilter, setActiveFilter] = useState<ToolCatalogFilter>(() =>
    parseCatalogFilter(initialFilter ?? null, filterOrder, showFilters),
  );
  const [activeSort, setActiveSort] = useState<ToolCatalogSort>(() =>
    parseCatalogSort(initialSort ?? null),
  );
  const [query, setQuery] = useState(initialQuery ?? '');
  // Seed with server-rendered summaries (ratings/usage) so cards paint with
  // their badges on the very first render. Without this the client fetch below
  // populated the badges a beat later, which read as the whole grid flickering.
  const [catalogLookup, setCatalogLookup] = useState<ToolCatalogLookup>(
    initialLookup ?? {
      summaries: {},
      viewerStates: {},
    },
  );
  const [visibleCount, setVisibleCount] = useState(CATALOG_INITIAL_VISIBLE);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const toolSlugList = useMemo(() => entries.map((entry) => entry.slug), [entries]);
  const favoriteSlugs = useMemo(
    () =>
      new Set(
        Object.entries(catalogLookup.viewerStates)
          .filter(([, state]) => state?.isFavorite)
          .map(([toolSlug]) => toolSlug as ToolCatalogItem['slug']),
      ),
    [catalogLookup.viewerStates],
  );

  useEffect(() => {
    if (!user && activeFilter === 'favorites') {
      setActiveFilter('all');
    }
  }, [activeFilter, user]);

  // Reset the visible window whenever the result set changes, so switching a
  // filter/sort or typing a query never leaves a stale "show more" offset.
  useEffect(() => {
    setVisibleCount(CATALOG_INITIAL_VISIBLE);
  }, [activeFilter, activeSort, deferredQuery]);

  // Intent-based prefetch for tool cards. The cards are plain server-rendered
  // Links (prefetch off) so they ship in the SSR HTML with no layout shift; this
  // delegated listener warms the tool route on hover/focus/touch, turning the
  // ~4s cold client navigation into a near-instant one — without a per-card
  // client component (which broke the cards' server render / caused CLS).
  useEffect(() => {
    const prefetched = new Set<string>();
    const warm = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const card = target.closest('a.tool-app-card');
      const href = card?.getAttribute('href');
      if (!href || prefetched.has(href)) {
        return;
      }
      prefetched.add(href);
      router.prefetch(href);
    };
    document.addEventListener('mouseover', warm, { passive: true });
    document.addEventListener('focusin', warm, { passive: true });
    document.addEventListener('touchstart', warm, { passive: true });
    return () => {
      document.removeEventListener('mouseover', warm);
      document.removeEventListener('focusin', warm);
      document.removeEventListener('touchstart', warm);
    };
  }, [router]);

  useEffect(() => {
    let isCancelled = false;

    async function loadCatalogLookup() {
      if (!toolSlugList.length) {
        return;
      }

      // Anonymous visitors get a fully static catalog: no per-user data to load,
      // so we skip the fetch entirely. That is what stops the grid from
      // flickering on first load (the client fetch used to repaint every card
      // with its rating a beat after hydration). Signed-in users still fetch to
      // layer in ratings + their favorites.
      if (!user) {
        return;
      }

      try {
        const headers: HeadersInit = {};

        if (user) {
          headers.authorization = `Bearer ${await user.getIdToken()}`;
        }

        const batches = Array.from(
          { length: Math.ceil(toolSlugList.length / TOOL_CATALOG_LOOKUP_BATCH_SIZE) },
          (_, batchIndex) =>
            toolSlugList.slice(
              batchIndex * TOOL_CATALOG_LOOKUP_BATCH_SIZE,
              (batchIndex + 1) * TOOL_CATALOG_LOOKUP_BATCH_SIZE,
            ),
        );

        const responses = await Promise.all(
          batches.map(async (batch) => {
            const response = await fetch(
              `/api/tools/catalog?toolSlugs=${encodeURIComponent(batch.join(','))}`,
              {
                method: 'GET',
                headers,
                cache: 'no-store',
              },
            );
            const payload = (await response.json()) as {
              success?: boolean;
              data?: ToolCatalogLookup;
            };

            if (!response.ok || !payload.success || !payload.data) {
              throw new Error('Could not load tool catalog summaries.');
            }

            return payload.data;
          }),
        );

        if (isCancelled) {
          return;
        }

        const nextLookup = responses.reduce<ToolCatalogLookup>(
          (accumulator, payload) => ({
            summaries: {
              ...accumulator.summaries,
              ...payload.summaries,
            },
            viewerStates: {
              ...accumulator.viewerStates,
              ...payload.viewerStates,
            },
          }),
          {
            summaries: {},
            viewerStates: {},
          },
        );

        setCatalogLookup(nextLookup);
      } catch {
        return;
      }
    }

    void loadCatalogLookup();

    return () => {
      isCancelled = true;
    };
  }, [toolSlugList, user, initialLookup]);

  const availableFilters = useMemo(
    () =>
      filterOrder.filter((filter) => {
        if (!showFilters && filter !== 'all') {
          return false;
        }

        if (filter === 'all') {
          return true;
        }

        if (filter === 'favorites') {
          return Boolean(user);
        }

        return entries.some((item) => item.category === filter);
      }),
    [entries, showFilters, user],
  );
  const currentSearch = searchParams.toString();
  // Tracks the search string we wrote ourselves, so the URL->state effect below
  // ignores our own router.replace and can never ping-pong with the write effect.
  const selfWrittenSearch = useRef<string | null>(null);

  useEffect(() => {
    if (currentSearch === selfWrittenSearch.current) {
      return;
    }

    const nextParams = new URLSearchParams(currentSearch);
    const nextFilter = parseCatalogFilter(nextParams.get('filter'), availableFilters, showFilters);
    const nextSort = parseCatalogSort(nextParams.get('sort'));
    const nextQuery = nextParams.get('q') ?? '';

    setActiveFilter((currentFilter) => (currentFilter === nextFilter ? currentFilter : nextFilter));
    setActiveSort((currentSort) => (currentSort === nextSort ? currentSort : nextSort));
    setQuery((currentQuery) => (currentQuery === nextQuery ? currentQuery : nextQuery));
  }, [availableFilters, currentSearch, showFilters]);

  useEffect(() => {
    const nextParams = new URLSearchParams(currentSearch);
    const normalizedQuery = query.trim();

    if (showFilters && activeFilter !== 'all' && availableFilters.includes(activeFilter)) {
      nextParams.set('filter', activeFilter);
    } else {
      nextParams.delete('filter');
    }

    if (activeSort !== 'recommended') {
      nextParams.set('sort', activeSort);
    } else {
      nextParams.delete('sort');
    }

    if (normalizedQuery) {
      nextParams.set('q', normalizedQuery);
    } else {
      nextParams.delete('q');
    }

    const nextSearch = nextParams.toString();

    if (nextSearch === currentSearch) {
      return;
    }

    selfWrittenSearch.current = nextSearch;
    router.replace(nextSearch ? `${pathname}?${nextSearch}` : pathname, { scroll: false });
  }, [
    activeFilter,
    activeSort,
    availableFilters,
    currentSearch,
    pathname,
    query,
    router,
    showFilters,
  ]);

  const sortedEntries = [...entries].sort((left, right) => {
    const leftSummary = catalogLookup.summaries[left.slug] ?? emptyCatalogSummary;
    const rightSummary = catalogLookup.summaries[right.slug] ?? emptyCatalogSummary;

    switch (activeSort) {
      case 'usage-desc':
        if (rightSummary.usageCount !== leftSummary.usageCount) {
          return rightSummary.usageCount - leftSummary.usageCount;
        }
        break;
      case 'usage-asc':
        if (leftSummary.usageCount !== rightSummary.usageCount) {
          return leftSummary.usageCount - rightSummary.usageCount;
        }
        break;
      case 'rating-desc': {
        const ratingDelta = compareByRating(leftSummary, rightSummary, 'desc');

        if (ratingDelta !== 0) {
          return ratingDelta;
        }
        break;
      }
      case 'rating-asc': {
        const ratingDelta = compareByRating(leftSummary, rightSummary, 'asc');

        if (ratingDelta !== 0) {
          return ratingDelta;
        }
        break;
      }
      case 'recommended':
      default: {
        // Stable, deterministic default: featured first, then alphabetical.
        // Usage/rating are intentionally NOT used here — those summaries load
        // client-side after render, so mixing them into the default order made
        // the catalog visibly reshuffle on load and differ between visits.
        // Live usage/rating are still available via the explicit sort options.
        const featuredDelta =
          Number(right.catalog.featured === true) - Number(left.catalog.featured === true);

        if (featuredDelta !== 0) {
          return featuredDelta;
        }
      }
    }

    return left.title.localeCompare(right.title);
  });

  const filteredEntries = sortedEntries.filter((item) => {
    if (!matchesFilter(item, activeFilter, favoriteSlugs)) {
      return false;
    }

    if (!deferredQuery) {
      return true;
    }

    return item.searchIndex.some((value) => value.toLowerCase().includes(deferredQuery));
  });

  const shownEntries = filteredEntries.slice(0, visibleCount);
  const hiddenCount = filteredEntries.length - shownEntries.length;

  const emptyState =
    activeFilter === 'favorites'
      ? {
          title: 'No saved tools yet',
          description: user
            ? 'Save tools from the catalog or a tool page to keep them in one place.'
            : 'Sign in to save tools to your account.',
        }
      : {
          title: 'No matching tools',
          description: 'Try a broader keyword, another filter, or a different sort.',
        };

  return (
    <section className={`tool-app-catalog ${className ?? ''}`.trim()}>
      {eyebrow || title || description ? (
        <div className="section-heading tool-app-catalog-heading">
          {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
          {title ? <h2>{title}</h2> : null}
          {description ? <p>{description}</p> : null}
        </div>
      ) : null}

      <div className="tool-app-catalog-toolbar">
        <label className="tool-app-catalog-search" htmlFor={searchId}>
          <ToolCatalogSearchIcon />
          <span className="sr-only">Search tools</span>
          <input
            id={searchId}
            type="search"
            value={query}
            placeholder="Search tools, jobs, and formats"
            onChange={(event) => {
              const nextValue = event.target.value;
              startTransition(() => setQuery(nextValue));
            }}
          />
          {query ? (
            <button
              type="button"
              className="tool-app-catalog-clear"
              aria-label="Clear search"
              onClick={() => {
                startTransition(() => setQuery(''));
              }}
            >
              <ToolCatalogClearIcon />
            </button>
          ) : null}
        </label>

        <div className="tool-app-catalog-controls">
          {showFilters ? (
            <div className="tool-app-catalog-filter-row" aria-label="Tool filters">
              {availableFilters.map((filter) => (
                <button
                  type="button"
                  aria-pressed={activeFilter === filter}
                  className={`tool-app-catalog-filter ${
                    activeFilter === filter ? 'tool-app-catalog-filter-active' : ''
                  }`}
                  key={filter}
                  onClick={() => {
                    startTransition(() => setActiveFilter(filter));
                  }}
                >
                  {filterLabels[filter]}
                </button>
              ))}
            </div>
          ) : null}

          <label className="tool-app-catalog-sort">
            <span>Sort</span>
            <select
              aria-label="Sort tools"
              value={activeSort}
              onChange={(event) => {
                const nextValue = event.target.value as ToolCatalogSort;
                startTransition(() => setActiveSort(nextValue));
              }}
            >
              {Object.entries(sortLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="tool-app-catalog-status">
          <span>{filteredEntries.length}</span>
          <p>
            {!showFilters || activeFilter === 'all'
              ? 'tools ready to open now'
              : `${filterLabels[activeFilter]} tools ready to open`}
          </p>
        </div>
      </div>

      <ToolCatalogCardGrid
        entries={shownEntries}
        summaries={catalogLookup.summaries}
        emptyState={emptyState}
        showRating={false}
      />

      {hiddenCount > 0 ? (
        <div className="blog-load-more">
          <button
            className="button button-ghost"
            onClick={() => setVisibleCount((current) => current + CATALOG_LOAD_MORE_STEP)}
            type="button"
          >
            Show more tools
          </button>
          <span className="blog-load-more-note">
            Showing {shownEntries.length.toLocaleString()} of{' '}
            {filteredEntries.length.toLocaleString()}
          </span>
        </div>
      ) : null}
    </section>
  );
}

export default function ToolAppCatalog(props: ToolAppCatalogProps) {
  return <ToolAppCatalogInner {...props} />;
}
